/**
 * Permission tiers — per-tab access levels. SHARED between the API and the SPA.
 *
 * Lives here rather than in apps/api because the Admin -> Permissions screen has
 * to parse and format the same grant strings the gates enforce. A second copy in
 * the frontend would drift, and this is security-relevant logic: a bare grant
 * meaning anything other than "full" would silently change what every existing
 * operator can do.
 *
 * ENCODING — string suffixes on the existing flat grant, deliberately:
 *
 *   "user-management"              -> full        (every existing grant, unchanged)
 *   "user-management:restricted"   -> restricted
 *   "user-management:read"         -> read
 *
 * Chosen over a nested object because it leaves the store shape
 * ({ userId: string[] }), the save-time allowlist, the whole-map CAS write and
 * the PermissionsTab payload ALL unchanged — which makes the migration a
 * genuine no-op: every grant that exists today already means full.
 */
export const TIERS = ["read", "restricted", "full"];
/** Higher rank satisfies lower: `full` passes a `read` requirement. */
const TIER_RANK = { read: 1, restricted: 2, full: 3 };
export function isTier(value) {
    return TIERS.includes(value);
}
/**
 * Split a stored grant into tab + tier. A bare tab means `full` (back-compat).
 * Returns null for a malformed grant rather than guessing — a grant nobody can
 * parse must not silently become full access.
 */
export function parseGrant(grant) {
    const raw = (grant ?? "").trim();
    if (!raw)
        return null;
    const colon = raw.indexOf(":");
    if (colon === -1)
        return { tab: raw, tier: "full" };
    const tab = raw.slice(0, colon).trim();
    const suffix = raw.slice(colon + 1).trim();
    if (!tab || !isTier(suffix))
        return null;
    return { tab, tier: suffix };
}
/**
 * The highest tier a set of grants confers on one tab, or null for no access.
 * Duplicate grants for the same tab resolve to the highest, so a save carrying
 * both "x" and "x:read" cannot accidentally downgrade someone.
 */
export function tierFor(grants, tab) {
    let best = null;
    for (const grant of grants ?? []) {
        const parsed = parseGrant(grant);
        if (!parsed || parsed.tab !== tab)
            continue;
        if (best === null || TIER_RANK[parsed.tier] > TIER_RANK[best])
            best = parsed.tier;
    }
    return best;
}
/** Does the tier someone holds satisfy the tier a route requires? */
export function meetsTier(held, required) {
    if (held === null)
        return false;
    return TIER_RANK[held] >= TIER_RANK[required];
}
/** Render a tab + tier back into its stored grant string. */
export function formatGrant(tab, tier) {
    return tier === "full" ? tab : `${tab}:${tier}`;
}
